import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Chip,
  IconButton,
  Collapse,
  Divider,
} from '@material-ui/core';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Print as PrintIcon,
  Business as BusinessIcon,
  Assignment as SerialIcon,
  FindInPage as RootCauseIcon,
  CheckCircle as ClosingIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';

import { RootState } from '../../store';
import {
  fetchNCRByIdStart,
  fetchDropdownsStart,
  saveGeneralDetailsStart,
  saveSerialsDispositionStart,
  saveClosingStart,
  addDefectStart,
  deleteDefectStart,
  setCurrentStep,
  clearError,
  setEditingDefect,
  setDefectDialogOpen,
  resetNCRState,
} from '../../store/reducers/ncrSlice';
import { NCR, PartDefect, AddDefectRequest, ncrApi } from '../../services/ncrApi';
import SignatureInput from '../Common/SignatureInput';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 'bold',
  },
  headerActions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  tabPanel: {
    padding: theme.spacing(3, 0),
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: '#01203a',
    color: 'white',
    borderRadius: theme.shape.borderRadius,
  },
  sectionIcon: {
    marginRight: theme.spacing(1),
  },
  logoContainer: {
    textAlign: 'center',
    marginBottom: theme.spacing(2),
  },
  formRow: {
    marginBottom: theme.spacing(2),
  },
  serialCard: {
    marginBottom: theme.spacing(2),
    border: '1px solid #ddd',
  },
  serialHeader: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(1, 2),
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  defectCard: {
    marginLeft: theme.spacing(2),
    marginBottom: theme.spacing(1),
    backgroundColor: '#01203a',
    color: 'white',
  },
  defectHeader: {
    padding: theme.spacing(1, 2),
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  defectContent: {
    padding: theme.spacing(2),
    backgroundColor: 'white',
    color: 'black',
  },
  addDefectButton: {
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(2),
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(3),
    padding: theme.spacing(2, 0),
  },
  leftActions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  rightActions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  readOnlyField: {
    backgroundColor: theme.palette.grey[100],
  },
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const NCRFill: React.FC = () => {
  const classes = useStyles();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    selectedNCR,
    selectedNCRLoading,
    selectedNCRError,
    dropdowns,
    currentStep,
    showClosingTab,
    showStepTwo,
    showStepThree,
    selectedSerials,
    selectedDefects,
    editingDefect,
    defectDialogOpen,
  } = useSelector((state: RootState) => state.ncr);

  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState<Partial<NCR>>({});
  const [expandedSerials, setExpandedSerials] = useState<Set<string>>(new Set());
  const [expandedDefects, setExpandedDefects] = useState<Set<string>>(new Set());
  const [newDefect, setNewDefect] = useState<Partial<AddDefectRequest>>({});

  useEffect(() => {
    if (id) {
      dispatch(resetNCRState());
      dispatch(fetchNCRByIdStart(id));
      dispatch(fetchDropdownsStart());
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (selectedNCR) {
      setFormData(selectedNCR);
    }
  }, [selectedNCR]);

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setActiveTab(newValue);
    dispatch(setCurrentStep(newValue + 1));
  };

  const handleInputChange = (field: keyof NCR) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSelectChange = (field: keyof NCR) => (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleCheckboxChange = (field: keyof NCR) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.checked,
    }));
  };

  const handleSignatureChange = (field: keyof NCR) => (signature: string | null) => {
    setFormData(prev => ({
      ...prev,
      [field]: signature,
    }));
  };

  const handleSaveGeneralDetails = () => {
    if (id) {
      dispatch(saveGeneralDetailsStart({
        ncrId: id,
        data: formData,
      }));
    }
  };

  const handleSaveSerialsDisposition = () => {
    if (id) {
      dispatch(saveSerialsDispositionStart({
        ncrId: id,
        serialData: selectedSerials,
      }));
    }
  };

  const handleSaveClosing = () => {
    if (id) {
      dispatch(saveClosingStart({
        ncrId: id,
        data: {
          user_approved_id: formData.user_approved_id || '',
          approved_date: formData.approved_date || '',
          ca_document: formData.ca_document,
          closure_sign: formData.closure_sign || '',
        },
      }));
    }
  };

  const handlePrintReport = async () => {
    if (selectedNCR) {
      try {
        const blob = await ncrApi.printReport(selectedNCR.id);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `NCR-${selectedNCR.ncr_folio_num}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Failed to download report:', error);
        // You could add error snackbar notification here if needed
      }
    }
  };

  const handleCancel = () => {
    navigate('/reports/ncr');
  };

  const handleErrorClose = () => {
    dispatch(clearError());
  };

  const toggleSerialExpansion = (serialId: string) => {
    setExpandedSerials(prev => {
      const newSet = new Set(prev);
      if (newSet.has(serialId)) {
        newSet.delete(serialId);
      } else {
        newSet.add(serialId);
      }
      return newSet;
    });
  };

  const toggleDefectExpansion = (defectId: string) => {
    setExpandedDefects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(defectId)) {
        newSet.delete(defectId);
      } else {
        newSet.add(defectId);
      }
      return newSet;
    });
  };

  const handleAddDefect = (serialId: string) => {
    const defectData: AddDefectRequest = {
      ncr_detail_id: serialId,
      nc_type_id: newDefect.nc_type_id || '',
      nc_sub_type_id: newDefect.nc_sub_type_id,
      part_attribute_id: newDefect.part_attribute_id || '',
      part_sec_attribute_id: newDefect.part_sec_attribute_id,
      part_terc_attribute_id: newDefect.part_terc_attribute_id,
      origin_dim_attribute: newDefect.origin_dim_attribute,
      actual_dim_attribute: newDefect.actual_dim_attribute,
      sight_gauge: newDefect.sight_gauge || false,
      cmm: newDefect.cmm || false,
      nogo_over_tolerance: newDefect.nogo_over_tolerance || false,
      nogo_under_tolerance: newDefect.nogo_under_tolerance || false,
    };

    dispatch(addDefectStart(defectData));
    setNewDefect({});
  };

  const handleDeleteDefect = (defectId: string) => {
    dispatch(deleteDefectStart(defectId));
  };

  const handleDocumentUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const base64Data = reader.result as string;
        const document = {
          id: '', // Will be set by backend
          file_name: file.name,
          file_path: '',
          file_data: base64Data,
          upload_date: new Date().toISOString(),
          uploaded_by: '', // Will be set by backend based on current user
        };
        
        setFormData(prev => ({
          ...prev,
          CADocument: document,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  if (selectedNCRLoading || !selectedNCR) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Typography variant="h4" className={classes.title}>
          Fill NCR - {selectedNCR.ncr_folio_num}
        </Typography>
        <Box className={classes.headerActions}>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrintReport}
          >
            Print
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="General Details" icon={<BusinessIcon />} />
          {showClosingTab && (
            <Tab label="Closing" icon={<ClosingIcon />} />
          )}
        </Tabs>

        {/* General Details Tab */}
        <TabPanel value={activeTab} index={0}>
          <Box className={classes.tabPanel}>
            {/* Company Logo and Header */}
            <Card className={classes.formRow}>
              <CardContent>
                <Box className={classes.sectionHeader}>
                  <Box className={classes.logoContainer} style={{ flex: 1 }}>
                    <img
                      src="/images/operoute-logo-wo.png"
                      width="300"
                      alt="OptiS Logo"
                      style={{ maxWidth: '100%' }}
                    />
                  </Box>
                  <Box style={{ flex: 1, textAlign: 'center' }}>
                    <Typography variant="h5">
                      Non-Conformance Report General Details
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="NCR No."
                      value={selectedNCR.ncr_folio_num}
                      InputProps={{ readOnly: true }}
                      className={classes.readOnlyField}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Current Status"
                      value={selectedNCR.closure_status}
                      InputProps={{ readOnly: true }}
                      className={classes.readOnlyField}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Applicable Concession"
                      value={formData.applicable_concession || ''}
                      onChange={handleInputChange('applicable_concession')}
                      placeholder="Concession"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Created By"
                      value={selectedNCR.created_by}
                      InputProps={{ readOnly: true }}
                      className={classes.readOnlyField}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Created Date"
                      value={new Date(selectedNCR.created_date).toLocaleDateString()}
                      InputProps={{ readOnly: true }}
                      className={classes.readOnlyField}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Part Description"
                      value={`${selectedNCR.part?.number} - ${selectedNCR.part?.description}`}
                      InputProps={{ readOnly: true }}
                      className={classes.readOnlyField}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Work Order Number"
                      value={selectedNCR.work_order?.wo_num}
                      InputProps={{ readOnly: true }}
                      className={classes.readOnlyField}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Route Card Number"
                      value={selectedNCR.route_card?.name}
                      InputProps={{ readOnly: true }}
                      className={classes.readOnlyField}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Operation Number"
                      value={`${selectedNCR.operation?.operation_num} - ${selectedNCR.operation?.name}`}
                      InputProps={{ readOnly: true }}
                      className={classes.readOnlyField}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Serials Detail Section */}
            <Card className={classes.formRow}>
              <CardContent>
                <Box className={classes.sectionHeader}>
                  <SerialIcon className={classes.sectionIcon} />
                  <Typography variant="h6">
                    Non-Conformance Report Serials Detail
                  </Typography>
                </Box>

                {selectedSerials.map((serial, index) => (
                  <Card key={serial.id} className={classes.serialCard}>
                    <Box
                      className={classes.serialHeader}
                      onClick={() => toggleSerialExpansion(serial.id)}
                    >
                      <Typography variant="h6">
                        Serial: {serial.serial_num}
                      </Typography>
                      <Box>
                        <Typography variant="body2" style={{ marginRight: 16 }}>
                          [Click to display]
                        </Typography>
                        {expandedSerials.has(serial.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </Box>
                    </Box>
                    <Collapse in={expandedSerials.has(serial.id)}>
                      <Box style={{ padding: 16 }}>
                        {serial.part_defects?.map((defect, defectIndex) => (
                          <Card key={defect.id} className={classes.defectCard}>
                            <Box
                              className={classes.defectHeader}
                              onClick={() => toggleDefectExpansion(defect.id)}
                            >
                              <Typography variant="body1">
                                Defect {defectIndex + 1}
                              </Typography>
                              {expandedDefects.has(defect.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </Box>
                            <Collapse in={expandedDefects.has(defect.id)}>
                              <Box className={classes.defectContent}>
                                <Button
                                  variant="contained"
                                  color="secondary"
                                  size="small"
                                  startIcon={<DeleteIcon />}
                                  onClick={() => handleDeleteDefect(defect.id)}
                                  style={{ marginBottom: 16 }}
                                >
                                  Delete
                                </Button>
                                
                                <Typography variant="subtitle2" gutterBottom>
                                  Non-Conformance Type
                                </Typography>
                                <Grid container spacing={2}>
                                  <Grid item xs={6}>
                                    <FormControl fullWidth disabled>
                                      <InputLabel>Non-Conformance Type</InputLabel>
                                      <Select value={defect.nc_type_id}>
                                        {dropdowns.ncTypes.map((type) => (
                                          <MenuItem key={type.id} value={type.id}>
                                            {type.name}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <FormControl fullWidth disabled>
                                      <InputLabel>Non-Conformance Sub Type</InputLabel>
                                      <Select value={defect.nc_sub_type_id}>
                                        {dropdowns.ncSubTypes.map((subType) => (
                                          <MenuItem key={subType.id} value={subType.id}>
                                            {subType.name}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                </Grid>

                                <Typography variant="subtitle2" gutterBottom style={{ marginTop: 16 }}>
                                  Non-Conformance Location
                                </Typography>
                                <Grid container spacing={2}>
                                  <Grid item xs={6}>
                                    <FormControl fullWidth disabled>
                                      <InputLabel>Primary Location</InputLabel>
                                      <Select value={defect.part_attribute_id}>
                                        {dropdowns.partAttributes.map((attr) => (
                                          <MenuItem key={attr.id} value={attr.id}>
                                            {attr.name}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <FormControl fullWidth disabled>
                                      <InputLabel>Secondary Location</InputLabel>
                                      <Select value={defect.part_sec_attribute_id}>
                                        {dropdowns.partSecAttributes.map((attr) => (
                                          <MenuItem key={attr.id} value={attr.id}>
                                            {attr.name}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <FormControl fullWidth disabled>
                                      <InputLabel>Tertiary Location</InputLabel>
                                      <Select value={defect.part_terc_attribute_id}>
                                        {dropdowns.partTercAttributes.map((attr) => (
                                          <MenuItem key={attr.id} value={attr.id}>
                                            {attr.name}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                </Grid>

                                <Grid container spacing={2} style={{ marginTop: 16 }}>
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth
                                      label="Drawing Dimension"
                                      value={defect.origin_dim_attribute || ''}
                                      InputProps={{ readOnly: true }}
                                    />
                                  </Grid>
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth
                                      label="Actual Dimension"
                                      value={defect.actual_dim_attribute || ''}
                                      InputProps={{ readOnly: true }}
                                    />
                                  </Grid>
                                </Grid>

                                <Grid container spacing={2} style={{ marginTop: 16 }}>
                                  <Grid item xs={6}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={defect.sight_gauge}
                                          disabled
                                        />
                                      }
                                      label="Sight Gauge"
                                    />
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={defect.cmm}
                                          disabled
                                        />
                                      }
                                      label="CMM"
                                    />
                                  </Grid>
                                  <Grid item xs={6}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={defect.nogo_over_tolerance}
                                          disabled
                                        />
                                      }
                                      label="Go/No-Go Above Tolerance Pass"
                                    />
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={defect.nogo_under_tolerance}
                                          disabled
                                        />
                                      }
                                      label="Go/No-Go Below Tolerance Fail"
                                    />
                                  </Grid>
                                </Grid>
                              </Box>
                            </Collapse>
                          </Card>
                        ))}
                        
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          startIcon={<AddIcon />}
                          className={classes.addDefectButton}
                          onClick={() => handleAddDefect(serial.id)}
                        >
                          Add Defect
                        </Button>
                      </Box>
                    </Collapse>
                  </Card>
                ))}
              </CardContent>
            </Card>

            {/* Root Cause Analysis Section */}
            <Card className={classes.formRow}>
              <CardContent>
                <Box className={classes.sectionHeader}>
                  <RootCauseIcon className={classes.sectionIcon} />
                  <Typography variant="h6">
                    Root Cause (5W's + 1H)
                  </Typography>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="What"
                      value={formData.what_txt || ''}
                      onChange={handleInputChange('what_txt')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Where"
                      value={formData.where_txt || ''}
                      onChange={handleInputChange('where_txt')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Machine</InputLabel>
                      <Select
                        value={formData.where_machine_id || ''}
                        onChange={handleSelectChange('where_machine_id')}
                      >
                        <MenuItem value="">
                          <em>Please Select An Option</em>
                        </MenuItem>
                        {dropdowns.machines.map((machine) => (
                          <MenuItem key={machine.id} value={machine.id}>
                            {machine.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Work Centre/Department</InputLabel>
                      <Select
                        value={formData.where_dept_id || ''}
                        onChange={handleSelectChange('where_dept_id')}
                      >
                        <MenuItem value="">
                          <em>Please Select An Option</em>
                        </MenuItem>
                        {dropdowns.departments.map((dept) => (
                          <MenuItem key={dept.id} value={dept.id}>
                            {dept.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Who"
                      value={formData.who_txt || ''}
                      onChange={handleInputChange('who_txt')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="When"
                      value={formData.when_txt || ''}
                      onChange={handleInputChange('when_txt')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Shift"
                      value={formData.stationtxt || ''}
                      onChange={handleInputChange('stationtxt')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Root Cause"
                      value={formData.root_cause_txt || ''}
                      onChange={handleInputChange('root_cause_txt')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Action Taken"
                      value={formData.action_taken || ''}
                      onChange={handleInputChange('action_taken')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Comments"
                      value={formData.comments || ''}
                      onChange={handleInputChange('comments')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <SignatureInput
                      label="Action Taken Responsible Signature"
                      value={formData.responsible_sign}
                      onChange={handleSignatureChange('responsible_sign')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <SignatureInput
                      label="Supervisor Signature"
                      value={formData.supervisor_sign}
                      onChange={handleSignatureChange('supervisor_sign')}
                    />
                  </Grid>
                </Grid>

                <Box className={classes.actions}>
                  <Box />
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveGeneralDetails}
                    disabled={selectedNCRLoading}
                  >
                    Save General Details
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </TabPanel>

        {/* Closing Tab */}
        {showClosingTab && (
          <TabPanel value={activeTab} index={1}>
            <Box className={classes.tabPanel}>
              <Card>
                <CardContent>
                  <Box className={classes.sectionHeader}>
                    <Box className={classes.logoContainer} style={{ flex: 1 }}>
                      <img
                        src="/images/operoute-logo-wo.png"
                        width="300"
                        alt="OptiS Logo"
                        style={{ maxWidth: '100%' }}
                      />
                    </Box>
                    <Box style={{ flex: 1, textAlign: 'center' }}>
                      <Typography variant="h5">
                        Non-Conformance Report Closing
                      </Typography>
                    </Box>
                  </Box>

                  <Typography variant="h6" gutterBottom style={{ marginTop: 24 }}>
                    Closing The Non-Conference
                  </Typography>
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={9}>
                      <FormControl fullWidth>
                        <InputLabel>Effectiveness Checked By QA Approval</InputLabel>
                        <Select
                          value={formData.user_approved_id || ''}
                          onChange={handleSelectChange('user_approved_id')}
                        >
                          <MenuItem value="">
                            <em>Select User</em>
                          </MenuItem>
                          {dropdowns.users.map((user) => (
                            <MenuItem key={user.id} value={user.id}>
                              {user.first_name} {user.last_name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        type="date"
                        label="Approval Date"
                        value={formData.approved_date || ''}
                        onChange={handleInputChange('approved_date')}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" gutterBottom>
                        Corrective Action Document
                      </Typography>
                      <Box border={1} borderColor="grey.300" p={2} borderRadius={1}>
                        <input
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          style={{ display: 'none' }}
                          id="ca-document-upload"
                          type="file"
                          onChange={handleDocumentUpload}
                        />
                        <label htmlFor="ca-document-upload">
                          <Button
                            variant="outlined"
                            component="span"
                            fullWidth
                            style={{ marginBottom: 8 }}
                          >
                            Upload Document
                          </Button>
                        </label>
                        {formData.ca_document && (
                          <Typography variant="body2" color="textSecondary">
                            Current file: {formData.ca_document.file_name}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <SignatureInput
                        label="Closure Signature"
                        value={formData.closure_sign}
                        onChange={handleSignatureChange('closure_sign')}
                      />
                    </Grid>
                  </Grid>

                  <Box className={classes.actions}>
                    <Box />
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveClosing}
                      disabled={selectedNCRLoading}
                    >
                      Save Closing
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </TabPanel>
        )}
      </Paper>

      {/* Main Actions */}
      <Box className={classes.actions}>
        <Button
          variant="outlined"
          startIcon={<CancelIcon />}
          onClick={handleCancel}
        >
          Close
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<PrintIcon />}
          onClick={handlePrintReport}
        >
          Print
        </Button>
      </Box>

      {/* Error Snackbar */}
      <Snackbar
        open={!!selectedNCRError}
        autoHideDuration={6000}
        onClose={handleErrorClose}
        message={selectedNCRError}
      />
    </Box>
  );
};

export default NCRFill; 